import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
    { name: 'Sign Up & Get 10% Off', path: '/business', highlight: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white/85 backdrop-blur-xl"
      data-testid="main-navigation"
    >
      {/* Top bar with contact info */}
      <div className="hidden md:block bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a 
              href="tel:+12129711334" 
              className="flex items-center gap-2 text-[#3EACDB] hover:text-[#2593C4] transition-colors font-medium"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              (212) 971-1334
            </a>
            <span className="flex items-center gap-2 text-[#3EACDB] font-medium">
              <MapPin className="w-4 h-4" />
              247 W 35th St, Suite #203, NYC
            </span>
          </div>
          <span className="text-[#3EACDB] font-medium">Mon-Fri: 9AM - 5:30PM · Sat-Sun: By Appointment</span>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-2">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center nav-logo"
            data-testid="logo-link"
          >
            <img 
              src="/images/charles-print-logo.png" 
              alt="Charles Print Logo"
              style={{ width: '200px', height: '128px' }}
              className="object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide uppercase transition-colors whitespace-nowrap ${
                  link.highlight
                    ? `px-3 py-1.5 rounded-sm border-2 border-[#E33135] text-[#E33135] hover:bg-[#E33135] hover:text-white ${isActive(link.path) ? 'bg-[#E33135] text-white' : ''}`
                    : isActive(link.path)
                      ? 'text-[#3EACDB]'
                      : 'text-zinc-600 hover:text-zinc-900'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s|%/g, '-').replace(/-+/g, '-').replace(/-$/, '')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a href="tel:+12129711334" data-testid="nav-call-btn">
              <Button variant="outline" className="h-10 px-4 text-sm font-semibold uppercase tracking-wider rounded-sm">
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
            </a>
            <Link to="/quote" data-testid="nav-quote-btn">
              <Button className="h-10 px-6 text-sm font-semibold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden border-t border-zinc-200 py-4"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-base font-medium tracking-wide uppercase py-2 ${
                    link.highlight
                      ? 'text-[#E33135] font-bold'
                      : isActive(link.path)
                        ? 'text-[#3EACDB]'
                        : 'text-zinc-600'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s|%/g, '-').replace(/-+/g, '-').replace(/-$/, '')}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-3 pt-4 border-t border-zinc-200">
                <a href="tel:+12129711334" className="w-full">
                  <Button variant="outline" className="w-full h-12 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Phone className="w-4 h-4 mr-2" />
                    (212) 971-1334
                  </Button>
                </a>
                <Link to="/quote" onClick={() => setIsOpen(false)} className="w-full">
                  <Button className="w-full h-12 text-sm font-semibold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
